import React, { useState, useEffect } from 'react';
import { Box, Flex, Heading, Text, Button, Image, VStack } from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import GoogleLoginButton from "./GoogleLogin";
import line from "../Images/Line.svg";
import axios from 'axios';
import AppSkeleton from './AppSkeleton';
import { BASE_API_URL } from './config';
import { ONBOARDING_VERSION } from "./onboardingVersion";
import { a, useAppColorMode } from './appColors';
import { getRecaptchaToken, preloadRecaptcha, recaptchaEnabled } from './recaptcha';
import { completeLogin, signInWithGoogle, isNoGoogleAccount, INSTRUCTOR_HOME, STUDENT_HOME } from './auth/completeLogin';
import { clearSession, hasSession } from './auth/session';
import { joinPath, pendingJoin } from './auth/pendingJoin';
import {
  ROLES,
  useRoleParam,
  primaryButtonStyle,
  secondaryButtonStyle,
  AuthField,
  RevealButton,
  RoleChooser,
  ChangeRole,
  AccentLink,
  AuthNotice,
  AuthPage,
  RecaptchaNotice,
} from './auth/AuthShell';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState(null);
  // Set when the credentials were right but the account is the other kind:
  // the role it actually is, so we can offer to switch.
  const [wrongRole, setWrongRole] = useState(null);
  // Google recognised them, but they have no Stande account yet.
  const [noGoogleAccount, setNoGoogleAccount] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { isDark, toggle } = useAppColorMode();
  const [role, chooseRole] = useRoleParam();

  // Back from confirming their email with a class invite waiting: invites are
  // for students, so skip the choice and go straight to the student login.
  // Once, on arrival, so "Change" still lets them pick the other role.
  useEffect(() => {
    if (!role && pendingJoin()) chooseRole("student");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  // A new choice starts clean.
  useEffect(() => {
    setLoginError(null);
    setWrongRole(null);
    setNoGoogleAccount(false);
  }, [role]);

  // Fetch the captcha script early so the first login isn't held up by it.
  useEffect(() => {
    preloadRecaptcha();
  }, []);

  // A session that can still be used: a live token, or one we can refresh.
  // A dead one left in storage must not send them into the app, where pages
  // would fire requests with no token before the route guard clears it.
  const isAuthenticated = hasSession();

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear out a dead session (say, from before refresh tokens) so nothing
      // mistakes it for the account they're about to log in to.
      if (localStorage.getItem('accessToken')) clearSession();
      return;
    }

    // Already signed in with a class invite waiting: finish joining it.
    const join = pendingJoin();
    if (join) {
      navigate(joinPath(join.token));
      return;
    }

    if (localStorage.getItem("userType") === "INSTRUCTOR") {
      navigate(INSTRUCTOR_HOME);
      return;
    }

    // Already signed in with a free paper waiting (e.g. they opened the email
    // link, then landed here) — go straight back to it.
    if (sessionStorage.getItem("freePaperSubject")) {
      navigate("/free-paper");
      return;
    }

    const completedVersion = localStorage.getItem("hasCompletedOnboardingVersion");
    navigate(completedVersion !== ONBOARDING_VERSION ? "/onboarding" : STUDENT_HOME);
  }, [isAuthenticated, navigate]);


  const handleLoginSuccess = async (session) => {
    try {
      const { wrongRole: actual } = await completeLogin({ session, role, navigate });
      if (actual) setWrongRole(actual);
    } catch (error) {
      console.error("Login success handling error:", error);
      setLoginError("An error occurred while processing login. Please try again.");
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email address").required("Email is required"),
      password: Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters long"),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      setLoginError(null);
      setWrongRole(null);
      try {
        // Mint a captcha token first. If it's switched on but can't run (script
        // blocked, offline), stop with something actionable rather than posting
        // a request the API will reject.
        const recaptchaToken = await getRecaptchaToken("login");
        if (recaptchaEnabled && !recaptchaToken) {
          setLoginError(
            "We couldn't run the security check. Disable any ad blocker and try again."
          );
          setIsLoading(false);
          return;
        }

        const formData = new FormData();
        formData.append('username', values.email);
        formData.append('password', values.password);
        if (recaptchaToken) {
          formData.append('recaptcha_token', recaptchaToken);
        }

        // Log in and retrieve access token
        const loginResponse = await axios.post(`${BASE_API_URL}/api/v1/auth/sign-in`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        await handleLoginSuccess(loginResponse.data);
      } catch (error) {
        console.error("Login error:", error);
        // The API returns 400 both for bad credentials and for a failed captcha;
        // surface the captcha message so a genuine user isn't told their
        // password is wrong when it isn't.
        const detail = error.response?.data?.detail;
        if (error.response?.status === 400 && typeof detail === "string" && /human/i.test(detail)) {
          setLoginError(detail);
        } else {
          setLoginError("Invalid email or password. Please try again.");
        }
      } finally {
        setIsLoading(false); // Hide loading screen
      }
    },
  });


  // Handle Google login success
  const handleGoogleLoginSuccess = async (response) => {
    setIsLoading(true);
    setLoginError(null);
    setWrongRole(null);
    setNoGoogleAccount(false);
    try {
      // Logging in never creates an account: new users go through sign-up,
      // where they agree to the terms.
      const data = await signInWithGoogle(response.code);
      await handleLoginSuccess(data);
    } catch (error) {
      if (isNoGoogleAccount(error)) {
        setNoGoogleAccount(true);
      } else {
        console.error("Google login error:", error);
        setLoginError("Google login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Google login failure
  const handleGoogleLoginFailure = () => {
    setLoginError("Google login failed. Please try again.");
  };

  const chooser = (
    <RoleChooser
      heading="Welcome to Stande"
      subheading="Choose how you'd like to log in."
      onChoose={chooseRole}
      isDark={isDark}
    />
  );

  const other = wrongRole && ROLES[wrongRole];

  const joining = pendingJoin();

  const form = role && (
    <>
      <ChangeRole role={role} onClick={() => chooseRole(null)} />
      {joining && (
        <Box mb="16px">
          <AuthNotice tone="warn" title={`Log in to join ${joining.courseName}`}>
            You'll be added to the space as soon as you're in.
          </AuthNotice>
        </Box>
      )}
      <Heading mb="4px" color={a.text} fontSize="24px" fontWeight="600">
        Log in as {ROLES[role].article}
      </Heading>
      <Text width="100%" color={a.textSub} fontSize="15px" fontWeight="300" mb={{ base: 5, md: 8 }}>
        {role === "instructor"
          ? "Log in to manage your spaces and review your students' work."
          : "Login to access unlimited exams for your chosen subjects and solve as well as get marked for free."}
      </Text>

      <form onSubmit={formik.handleSubmit}>
        <VStack alignItems="left" spacing={{ base: 4, md: 8 }}>
          <AuthField formik={formik} name="email" label="Email" type="email" autoComplete="email" />
          <AuthField
            formik={formik}
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            rightElement={<RevealButton shown={showPassword} onToggle={togglePasswordVisibility} />}
          />
        </VStack>

        {other && (
          <Box
            mt={{ base: 4, md: 6 }}
            padding="16px 20px"
            borderRadius="14px"
            border="1px solid"
            borderColor={a.border}
            background={a.warnBg}
            textAlign="left"
          >
            <Text fontSize="15px" fontWeight="500" color={a.text}>
              This is {ROLES[wrongRole].article} account
            </Text>
            <Text fontSize="13px" fontWeight="300" color={a.textSub} mt="2px">
              You can't log in to it as {ROLES[role].article}.
              Switch to {other.title.toLowerCase()} and log in again.
            </Text>
            <Button
              onClick={() => chooseRole(wrongRole)}
              mt="12px"
              px="16px"
              py="8px"
              h="auto"
              fontSize="13px"
              fontWeight="500"
              borderRadius="12px"
              bg={a.surface}
              color={a.text}
              border="1px solid"
              borderColor={a.border}
              _hover={{ bg: a.hover }}
            >
              Log in as {other.article}
            </Button>
          </Box>
        )}

        {loginError && <Text color="red.500" mt={4}>{loginError}</Text>}

        <Button type="submit" sx={primaryButtonStyle} mt={{ base: 5, md: 8 }} display="inline-flex">
          <Flex align="center" justify="center" gap="2px">
            <Text>Login</Text>
          </Flex>
        </Button>
        <RecaptchaNotice />
      </form>

      <Link to={`/create-account?as=${role}`}>
        <Button sx={secondaryButtonStyle} mt={4} display="inline-flex">
          <Flex align="center" justify="center" gap="2px">
            <Text>Create Account</Text>
          </Flex>
        </Button>
      </Link>

      <Flex align="center" justifyContent="center" mt={{ base: 5, md: 8 }} mb={{ base: 6, md: 14 }}>
        <Image src={line} alt="Line-svg" />
        <Text color={a.textSub} fontSize="14px" mx={4} whiteSpace="nowrap">
          or
        </Text>
        <Image src={line} alt="Line-svg" />
      </Flex>

      <GoogleLoginButton
        onSuccess={handleGoogleLoginSuccess}
        onError={handleGoogleLoginFailure}
      />

      {noGoogleAccount && (
        <Box mt="14px">
          <AuthNotice tone="warn" title="No account for that Google address yet">
            Create one first — it only takes a moment.{" "}
            <Link to={`/create-account?as=${role}`}>
              <AccentLink>Create {ROLES[role].article} account</AccentLink>
            </Link>
          </AuthNotice>
        </Box>
      )}
    </>
  );

  return (
    <>
      {isLoading && <AppSkeleton />}
      <AuthPage isDark={isDark} toggle={toggle} wide={!role}>
        {role ? form : chooser}
      </AuthPage>
    </>
  );
};

export default Login;
